<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  messageTimestamp,
  messageTimestampSmart,
} from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useMessageSender } from '../../useMessageSender.js';
import Icon from 'next/icon/Icon.vue';

const { contentAttributes, status, createdAt } = useMessageContext();
const currentChat = useMapGetter('getSelectedChat');
const {
  name: senderName,
  fromEmail,
  hasDistinctFromAddress,
} = useMessageSender();

const isOpen = ref(false);
const panelId = computed(() => `email-meta-${createdAt.value}`);

const hasError = computed(() => status.value === MESSAGE_STATUS.FAILED);

const toEmails = computed(() => {
  const { toEmails: top, email } = contentAttributes.value || {};
  return email?.to ?? top ?? [];
});

const ccEmails = computed(
  () =>
    contentAttributes.value?.ccEmails ??
    contentAttributes.value?.email?.cc ??
    []
);

const bccEmails = computed(
  () =>
    contentAttributes.value?.bccEmails ??
    contentAttributes.value?.email?.bcc ??
    []
);

const subject = computed(
  () =>
    contentAttributes.value?.email?.subject ??
    currentChat.value?.additional_attributes?.front_subject ??
    ''
);

const shortTime = computed(() => messageTimestampSmart(createdAt.value));
const absoluteTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, yyyy h:mm a')
);
const isoTime = computed(() =>
  createdAt.value ? new Date(createdAt.value * 1000).toISOString() : ''
);

const showMeta = computed(
  () => !!fromEmail.value || !!senderName.value || !!subject.value
);

const hasRecipients = computed(
  () => toEmails.value.length || ccEmails.value.length || bccEmails.value.length
);

// Bloom: only show the expand affordance if there's actually something
// to expand. Imported Front messages don't carry recipient lists on the
// message row, so the chevron used to flap with no panel underneath.
const hasExpandable = computed(() => hasRecipients.value || !!subject.value);
</script>

<template>
  <!--
    Bloom: Front-style email header. The avatar lives at the EmailBubble
    level (so meta header + body share its 32px column). Here we render
    the inline "name <email> [chevron]  ...  timestamp" row plus a
    collapsible recipient/subject panel below.
  -->
  <section v-show="showMeta" class="space-y-0.5">
    <div class="flex items-baseline gap-2">
      <component
        :is="hasExpandable ? 'button' : 'div'"
        :type="hasExpandable ? 'button' : undefined"
        :aria-expanded="hasExpandable ? isOpen : undefined"
        :aria-controls="hasExpandable ? panelId : undefined"
        class="flex-1 min-w-0 text-left flex items-baseline gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-7"
        :class="hasExpandable ? 'cursor-pointer' : ''"
        @click="hasExpandable && (isOpen = !isOpen)"
      >
        <span
          class="font-semibold truncate"
          :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-12'"
        >
          {{ senderName }}
        </span>
        <span
          v-if="hasDistinctFromAddress && fromEmail"
          class="truncate text-xs text-n-slate-10"
        >
          &lt;{{ fromEmail }}&gt;
        </span>
        <Icon
          v-if="hasExpandable"
          :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="size-3 text-n-slate-10 shrink-0"
          aria-hidden="true"
        />
      </component>
      <time
        v-tooltip.left="absoluteTime"
        :datetime="isoTime"
        class="shrink-0 text-xs text-n-slate-10 whitespace-nowrap"
      >
        {{ shortTime }}
      </time>
    </div>
    <div
      v-if="hasRecipients && !isOpen"
      class="text-xs text-n-slate-10 truncate"
    >
      <span v-if="toEmails.length">
        {{ $t('EMAIL_HEADER.TO') }}:
        <span class="text-n-slate-11">{{ toEmails.join(', ') }}</span>
      </span>
      <span v-if="ccEmails.length" class="ml-2">
        {{ $t('EMAIL_HEADER.CC') }}:
        <span class="text-n-slate-11">{{ ccEmails.join(', ') }}</span>
      </span>
    </div>
    <div
      v-if="isOpen"
      :id="panelId"
      class="mt-1 space-y-0.5 text-xs text-n-slate-11"
    >
      <div v-if="toEmails.length">
        <span class="text-n-slate-10">{{ $t('EMAIL_HEADER.TO') }}:</span>
        {{ toEmails.join(', ') }}
      </div>
      <div v-if="ccEmails.length">
        <span class="text-n-slate-10">{{ $t('EMAIL_HEADER.CC') }}:</span>
        {{ ccEmails.join(', ') }}
      </div>
      <div v-if="bccEmails.length">
        <span class="text-n-slate-10">{{ $t('EMAIL_HEADER.BCC') }}:</span>
        {{ bccEmails.join(', ') }}
      </div>
      <div v-if="subject">
        <span class="text-n-slate-10">{{ $t('EMAIL_HEADER.SUBJECT') }}:</span>
        {{ subject }}
      </div>
    </div>
  </section>
</template>
